import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  LinearProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RemoveCircle as RemoveCircleIcon,
  HelpOutline as HelpOutlineIcon,
  Cake as CakeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { UserDetailProfile } from '../../types/admin';
import { UserRole, UserStatus } from '../../types/auth';
import { GraduationStatus } from '../../types/profile';
import { getPrefectureName, getJobTypeName } from '../../constants/prefectures';
import { getIntegratedUserStatus } from '../MyPage/Profile/utils';

interface UserProfileDetailModalProps {
  open: boolean;
  onClose: () => void;
  userId: number;
}

export const UserProfileDetailModal: React.FC<UserProfileDetailModalProps> = ({
  open,
  onClose,
  userId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [userProfile, setUserProfile] = useState<UserDetailProfile | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && userId) {
      fetchUserProfile();
    }
  }, [open, userId]);

  const fetchUserProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      // タイムアウト設定（60秒）とアボートコントローラー
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 60000);

      console.log(`📖 [ADMIN] Fetching profile for user ${userId}...`);

      const response = await axios.get(`/api/admin/users/${userId}/profile`, {
        signal: controller.signal,
        timeout: 60000, // axios タイムアウト
      });

      clearTimeout(timeoutId);
      console.log(`✅ [ADMIN] Profile loaded successfully for user ${userId}`);
      setUserProfile(response.data.data);
    } catch (err) {
      console.error(`🚨 [ADMIN] Profile fetch failed for user ${userId}:`, err);

      let errorMessage = 'ユーザープロフィールの取得に失敗しました';

      if (axios.isAxiosError(err)) {
        if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          errorMessage =
            'プロフィールの読み込みがタイムアウトしました。このユーザーはチャット数が多いため、データの読み込みに時間がかかっています。しばらく時間をおいてから再度お試しください。';
        } else if (err.response?.status === 404) {
          errorMessage = 'ユーザーが見つかりません';
        } else if (err.response?.status && err.response.status >= 500) {
          errorMessage =
            'サーバーエラーが発生しました。管理者にお問い合わせください。';
        } else {
          errorMessage =
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            'ユーザープロフィールの取得に失敗しました';
        }
      } else if (err instanceof Error && err.name === 'AbortError') {
        errorMessage = 'リクエストがキャンセルされました';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value: number | undefined): string => {
    if (value === undefined || value === null) return '未設定';
    return value.toLocaleString('ja-JP');
  };

  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '未設定';
    return `¥${value.toLocaleString('ja-JP')}`;
  };

  const formatNumberWithStyle = (value: number | undefined) => {
    if (value === undefined || value === null) {
      return {
        text: '未設定',
        isSet: false,
      };
    }
    return {
      text: value.toLocaleString('ja-JP'),
      isSet: true,
    };
  };

  const formatCurrencyWithStyle = (value: number | undefined) => {
    if (value === undefined || value === null) {
      return {
        text: '未設定',
        isSet: false,
      };
    }
    return {
      text: `¥${value.toLocaleString('ja-JP')}`,
      isSet: true,
    };
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '未設定';
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  const formatQualifications = (
    qualifications: string | undefined
  ): string[] => {
    if (!qualifications) return [];
    try {
      return JSON.parse(qualifications);
    } catch {
      return qualifications.split(',').map((q) => q.trim());
    }
  };

  const getGraduationStatusLabel = (status: string | undefined): string => {
    if (!status) return '未設定';
    switch (status) {
      case GraduationStatus.CURRENT:
        return '在学中';
      case GraduationStatus.EXPECTED:
        return '卒業予定';
      case GraduationStatus.GRADUATED:
        return '卒業済み';
      default:
        return '未設定';
    }
  };

  const calculateAge = (birthDate: string | undefined): string => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return ` (${age}歳)`;
  };

  const getStatusChip = (status: UserStatus, isVerified: boolean) => {
    const statusInfo = getIntegratedUserStatus(status, isVerified);
    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color}
        size="small"
        variant="filled"
      />
    );
  };

  const getRoleChip = (role: UserRole) => {
    const getRoleLabel = (role: UserRole) => {
      switch (role) {
        case UserRole.ADMIN:
          return '管理者';
        case UserRole.COMPANY_ADMIN:
          return '企業管理者';
        case UserRole.HR:
          return 'HR担当者';
        case UserRole.EMPLOYEE:
          return '従業員';
        case UserRole.STUDENT:
        default:
          return '一般ユーザー';
      }
    };

    return (
      <Chip
        label={getRoleLabel(role)}
        color={role === UserRole.ADMIN ? 'primary' : 'default'}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>ユーザープロフィール詳細</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              プロフィールを読み込み中...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>ユーザープロフィール詳細</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>閉じる</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!userProfile) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            ユーザープロフィール詳細
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          {/* 基本情報 */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  基本情報
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar
                      src={userProfile.avatarUrl}
                      sx={{ width: 64, height: 64, mr: 2 }}
                    >
                      {userProfile.fullName.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 0.5,
                        }}
                      >
                        {userProfile.fullName}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                        }}
                      >
                        ID: {userProfile.id}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    sx={{ mt: 1 }}
                  >
                    {getRoleChip(userProfile.userRole)}
                    {getStatusChip(userProfile.status, userProfile.isVerified)}
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <EmailIcon
                      sx={{
                        mr: 1.5,
                        color: 'primary.main',
                        fontSize: '1.2rem',
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          display: 'block',
                          mb: 0.25,
                        }}
                      >
                        メールアドレス
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: 'text.primary',
                        }}
                      >
                        {userProfile.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PhoneIcon
                      sx={{
                        mr: 1.5,
                        color: 'primary.main',
                        fontSize: '1.2rem',
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          display: 'block',
                          mb: 0.25,
                        }}
                      >
                        電話番号
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: userProfile.phone
                            ? 'text.primary'
                            : 'text.secondary',
                          fontStyle: userProfile.phone ? 'normal' : 'italic',
                        }}
                      >
                        {userProfile.phone || '未設定'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <BusinessIcon
                      sx={{
                        mr: 1.5,
                        color: 'primary.main',
                        fontSize: '1.2rem',
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          display: 'block',
                          mb: 0.25,
                        }}
                      >
                        組織・会社
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: userProfile.organization
                            ? 'text.primary'
                            : 'text.secondary',
                          fontStyle: userProfile.organization
                            ? 'normal'
                            : 'italic',
                        }}
                      >
                        {userProfile.organization || '未設定'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LocationIcon
                      sx={{
                        mr: 1.5,
                        color: 'primary.main',
                        fontSize: '1.2rem',
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          display: 'block',
                          mb: 0.25,
                        }}
                      >
                        現在地
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: userProfile.currentPrefecture
                            ? 'text.primary'
                            : 'text.secondary',
                          fontStyle: userProfile.currentPrefecture
                            ? 'normal'
                            : 'italic',
                        }}
                      >
                        {userProfile.currentPrefecture
                          ? getPrefectureName(userProfile.currentPrefecture)
                          : '未設定'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {userProfile.bio && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      自己紹介
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.6,
                        color: 'text.primary',
                      }}
                    >
                      {userProfile.bio}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* 営業実績・経験情報 */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  営業実績・経験情報
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      仲介実績
                    </Typography>
                    {(() => {
                      const formatted = formatNumberWithStyle(
                        userProfile.mediationCount
                      );
                      return (
                        <Typography
                          variant={formatted.isSet ? 'h5' : 'body1'}
                          sx={{
                            fontWeight: formatted.isSet ? 600 : 500,
                            color: formatted.isSet
                              ? 'text.primary'
                              : 'text.secondary',
                            fontStyle: formatted.isSet ? 'normal' : 'italic',
                            fontSize: formatted.isSet
                              ? { xs: '1.5rem', sm: '2rem' }
                              : '1rem',
                          }}
                        >
                          {formatted.text}
                          {formatted.isSet && (
                            <Typography
                              component="span"
                              variant="body1"
                              sx={{
                                ml: 0.5,
                                fontWeight: 400,
                                color: 'text.secondary',
                              }}
                            >
                              件
                            </Typography>
                          )}
                        </Typography>
                      );
                    })()}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      月間最高売り上げ
                    </Typography>
                    {(() => {
                      const formatted = formatCurrencyWithStyle(
                        userProfile.monthlyMaxSales
                      );
                      return (
                        <Typography
                          variant={formatted.isSet ? 'h5' : 'body1'}
                          sx={{
                            fontWeight: formatted.isSet ? 600 : 500,
                            color: formatted.isSet
                              ? 'text.primary'
                              : 'text.secondary',
                            fontStyle: formatted.isSet ? 'normal' : 'italic',
                            fontSize: formatted.isSet
                              ? { xs: '1.5rem', sm: '2rem' }
                              : '1rem',
                          }}
                        >
                          {formatted.text}
                        </Typography>
                      );
                    })()}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      総売り上げ
                    </Typography>
                    {(() => {
                      const formatted = formatCurrencyWithStyle(
                        userProfile.totalSales
                      );
                      return (
                        <Typography
                          variant={formatted.isSet ? 'h5' : 'body1'}
                          sx={{
                            fontWeight: formatted.isSet ? 600 : 500,
                            color: formatted.isSet
                              ? 'text.primary'
                              : 'text.secondary',
                            fontStyle: formatted.isSet ? 'normal' : 'italic',
                            fontSize: formatted.isSet
                              ? { xs: '1.5rem', sm: '2rem' }
                              : '1rem',
                          }}
                        >
                          {formatted.text}
                        </Typography>
                      );
                    })()}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      不動産歴
                    </Typography>
                    {(() => {
                      const months = userProfile.realEstateExperienceMonths;
                      const hasExperience = months && months > 0;
                      return (
                        <Typography
                          variant={hasExperience ? 'h5' : 'body1'}
                          sx={{
                            fontWeight: hasExperience ? 600 : 500,
                            color: hasExperience
                              ? 'text.primary'
                              : 'text.secondary',
                            fontStyle: hasExperience ? 'normal' : 'italic',
                            fontSize: hasExperience
                              ? { xs: '1.5rem', sm: '2rem' }
                              : '1rem',
                          }}
                        >
                          {hasExperience
                            ? months.toLocaleString('ja-JP')
                            : '未設定'}
                          {hasExperience && (
                            <Typography
                              component="span"
                              variant="body1"
                              sx={{
                                ml: 0.5,
                                fontWeight: 400,
                                color: 'text.secondary',
                              }}
                            >
                              ヶ月
                            </Typography>
                          )}
                        </Typography>
                      );
                    })()}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      起業志向
                    </Typography>
                    <Box display="flex" alignItems="center">
                      {userProfile.entrepreneurialInterest === true ? (
                        <CheckCircleIcon
                          color="success"
                          sx={{ mr: 1, fontSize: '1.25rem' }}
                        />
                      ) : userProfile.entrepreneurialInterest === false ? (
                        <RemoveCircleIcon
                          sx={{
                            mr: 1,
                            fontSize: '1.25rem',
                            color: 'text.secondary',
                          }}
                        />
                      ) : (
                        <HelpOutlineIcon
                          sx={{
                            mr: 1,
                            fontSize: '1.25rem',
                            color: 'text.disabled',
                          }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color:
                            userProfile.entrepreneurialInterest === true
                              ? 'success.main'
                              : userProfile.entrepreneurialInterest === false
                                ? 'text.secondary'
                                : 'text.disabled',
                        }}
                      >
                        {userProfile.entrepreneurialInterest === true
                          ? 'あり'
                          : userProfile.entrepreneurialInterest === false
                            ? 'なし'
                            : '未選択'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* キャリア・希望情報 */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <WorkIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  キャリア・希望情報
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      現在のエリア
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {userProfile.currentPrefecture
                        ? getPrefectureName(userProfile.currentPrefecture)
                        : '未設定'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      希望エリア
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {userProfile.desiredPrefecture
                        ? getPrefectureName(userProfile.desiredPrefecture)
                        : '未設定'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      希望職種
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {userProfile.desiredJobType
                        ? getJobTypeName(userProfile.desiredJobType)
                        : '未設定'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      資格
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {formatQualifications(userProfile.qualifications).length >
                      0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {formatQualifications(userProfile.qualifications).map(
                            (qualification, index) => (
                              <Chip
                                key={index}
                                label={qualification}
                                size="small"
                                variant="outlined"
                                icon={<SchoolIcon />}
                                sx={{
                                  '& .MuiChip-label': {
                                    fontWeight: 500,
                                  },
                                }}
                              />
                            )
                          )}
                        </Box>
                      ) : (
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 400,
                            color: 'text.secondary',
                            fontStyle: 'italic',
                          }}
                        >
                          未設定
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* 学歴情報 */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <SchoolIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  学歴情報
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      大学名
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {userProfile.university || '未設定'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      学部・学科名
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {userProfile.faculty || '未設定'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      卒業年
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {userProfile.graduationYear
                        ? `${userProfile.graduationYear}年`
                        : '未設定'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      卒業状況
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {getGraduationStatusLabel(userProfile.graduationStatus)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      生年月日・年齢
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {userProfile.birthDate
                        ? `${formatDate(userProfile.birthDate)}${calculateAge(userProfile.birthDate)}`
                        : '未設定'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* システム情報・統計 */}
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  システム情報・統計
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      最終ログイン
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {formatDate(userProfile.lastLoginAt)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      登録日
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      {formatDate(userProfile.createdAt)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      backgroundColor: 'primary.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'primary.100',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="primary.main"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      チャットセッション数
                    </Typography>
                    <Typography
                      variant="h4"
                      color="primary.main"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.75rem', sm: '2.5rem' },
                      }}
                    >
                      {formatNumber(userProfile.totalChatSessions)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      backgroundColor: 'success.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'success.100',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      メッセージ数
                    </Typography>
                    <Typography
                      variant="h4"
                      color="success.main"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.75rem', sm: '2.5rem' },
                      }}
                    >
                      {formatNumber(userProfile.totalMessages)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      backgroundColor: 'warning.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'warning.100',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      使用トークン数
                    </Typography>
                    <Typography
                      variant="h4"
                      color="warning.main"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.75rem', sm: '2.5rem' },
                      }}
                    >
                      {formatNumber(userProfile.totalTokensUsed)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            minWidth: 120,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};
